// Turns the Gantt answer into rows: grants as group headers, their tasks under
// them, tasks under no grant last. Pure, so it is tested without a browser.
import { daysUntil, GRANT_TYPE, hubDate, isCalendarDate, lowerKey, type ActionItem, type GanttBundle, type GanttNode, type GanttPerson } from '@pathonline/shared';

export type Scope = 'my' | 'all';

export interface Filters {
  scope: Scope;
  /** Status values to show; empty = all. */
  statuses: string[];
  includeUndated: boolean;
  /** Task categories to show; empty = all. */
  types: string[];
  /** ProjectID, or '' for every programme. */
  programme: string;
  /** Under "All": only tasks this person is on; '' = anyone. */
  person: string;
  /** Roles whose people show as avatars on the bars. */
  roles: string[];
  /** People below this Focus are not drawn on the bars. */
  minFocus: number;
}

export interface Row {
  kind: 'grant' | 'task' | 'nogrant';
  key: string;
  node?: GanttNode;
  depth: number;
  /** The grant group a task row sits in ('' = none). */
  group: string;
}

/** A link we may put in an href: http(s) only, never javascript: or data: (the server stores only these). */
export const isHttpLink = (v: string | undefined): v is string => !!v && /^https?:\/\/\S+$/i.test(v);

/**
 * The first http(s) link in a cell, or undefined. OneNote's "copy link" gives two, with a space between:
 * "https://… onenote:https://…"; the imported OneNote cells hold that.
 */
export const httpLinkOf = (v: string | undefined): string | undefined => v?.split(/\s+/).find(isHttpLink);

/** A link field's check before it saves: '' = fine (empty clears it). The server refuses any other link. */
export const httpCheck = (v: string) => (!v || isHttpLink(v) ? '' : 'Paste a full link starting with https://');

/** The roles in the old display order. */
export const ROLE_ORDER = ['leads', 'supervises', 'contributes', 'reviews'];
export const byRole = (x: { role: string }, y: { role: string }) => ROLE_ORDER.indexOf(x.role) - ROLE_ORDER.indexOf(y.role);
/** Place in ROLE_ORDER, an unknown role last (Comments, Network; byRole puts it first). */
export const roleRank = (r: string) => {
  const i = ROLE_ORDER.indexOf(r);
  return i < 0 ? 9 : i;
};

export const DEFAULT_FILTERS: Filters = {
  scope: 'my',
  statuses: ['ACTIVE'],
  includeUndated: false,
  types: [],
  programme: '',
  person: '',
  roles: [...ROLE_ORDER],
  minFocus: 3,
};

export const isGrant = (n: GanttNode) => n.type === GRANT_TYPE;
export const isDated = (n: GanttNode) => !!(n.start || n.end);

/**
 * Gantt write on this node. A scoped editor (SelfFilter) never changes a grant; the server refuses it too.
 * `scoped` is the Gantt row's SelfFilter; the server checks the write scope (writeScopeOf). They differ
 * only for a role whose SelfFilter sits on another row that gives write; there the server refuses the save.
 */
export const canEditNode = (b: GanttBundle, n: GanttNode) => b.canEdit && !(isGrant(n) && b.scoped);

/** NodeIDs the signed-in person is assigned to. */
export function myNodeIds(b: GanttBundle): Set<string> {
  const me = b.me.person.trim().toLowerCase();
  return new Set(b.assignments.filter((a) => me && a.person.trim().toLowerCase() === me).map((a) => a.nodeId));
}

/** The grants a task is listed under: all its own grants, else all of its nearest ancestor's. */
function groupGrants(n: GanttNode, byId: Map<string, GanttNode>): string[] {
  const seen = new Set<string>();
  let cur: GanttNode | undefined = n;
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    const own = cur.grantIds.filter(Boolean);
    if (own.length) return own;
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  return [];
}

const byStartThenLabel = (a: GanttNode, b: GanttNode) =>
  (a.start || a.end || '9999').localeCompare(b.start || b.end || '9999') || a.label.localeCompare(b.label);

/** Grant groups, old byStart (DP:7123, 7139): by start date, no start last, then by name. */
const byGrantStart = (a: GanttNode, b: GanttNode) =>
  Number(!a.start) - Number(!b.start) || a.start.localeCompare(b.start) || a.label.localeCompare(b.label);

/**
 * A grant group's tasks as a tree, the old Gantt's order: each top-level task (by start
 * date), then its own children right under it, and so on. A task whose parent is not in
 * this group starts at the top level here. Indent stops at 4 levels.
 */
function nested(tasks: GanttNode[]): { node: GanttNode; depth: number }[] {
  const inGroup = new Set(tasks.map((t) => t.id));
  const kids = new Map<string, GanttNode[]>();
  const roots: GanttNode[] = [];
  for (const t of tasks) {
    if (t.parentId && t.parentId !== t.id && inGroup.has(t.parentId)) kids.set(t.parentId, [...(kids.get(t.parentId) ?? []), t]);
    else roots.push(t);
  }
  const out: { node: GanttNode; depth: number }[] = [];
  const placed = new Set<string>();
  const add = (n: GanttNode, depth: number) => {
    if (placed.has(n.id)) return;
    placed.add(n.id);
    out.push({ node: n, depth: Math.min(depth, 4) });
    for (const c of (kids.get(n.id) ?? []).sort(byStartThenLabel)) add(c, depth + 1);
  };
  for (const r of roots.sort(byStartThenLabel)) add(r, 0);
  // A parent loop has no top-level task: show its members at the top level rather than lose them.
  for (const t of [...tasks].sort(byStartThenLabel)) add(t, 0);
  return out;
}


/** NodeIDs a person is on. */
function involving(b: GanttBundle, person: string): Set<string> {
  const k = person.trim().toLowerCase();
  return new Set(b.assignments.filter((a) => a.person.trim().toLowerCase() === k).map((a) => a.nodeId));
}

/**
 * Rows for the chart. `focus` = a grant id ("Focus & expand"): only that grant's
 * tasks, and the person scope is lifted inside it. Type, status and programme still apply.
 */
export function buildRows(b: GanttBundle, f: Filters, focus?: string): Row[] {
  const byId = new Map(b.nodes.map((n) => [n.id, n]));
  const focused = focus && b.canFocus && byId.get(focus) && isGrant(byId.get(focus)!) ? focus : '';
  // No person on the account: "My" would be empty, so an unscoped account sees everything.
  const scope: Scope = b.scoped ? 'my' : b.me.person ? f.scope : 'all';
  const who = focused ? null : scope === 'my' ? myNodeIds(b) : f.person ? involving(b, f.person) : null;

  const tasks = b.nodes.filter(
    (n) =>
      !isGrant(n) &&
      (!who || who.has(n.id)) &&
      (f.types.length === 0 || f.types.includes(n.type || 'Topic')) &&
      (!f.programme || n.projectId === f.programme) &&
      (f.statuses.length === 0 || f.statuses.includes(n.status)) &&
      (f.includeUndated || isDated(n)),
  );

  // A task on two grants is listed under each of them (old Gantt); a grant not on this Gantt counts as none.
  const groups = new Map<string, GanttNode[]>();
  for (const t of tasks) {
    const known = [...new Set(groupGrants(t, byId))].filter((g) => byId.has(g));
    for (const key of known.length ? known : ['']) {
      if (focused && key !== focused) continue;
      groups.set(key, [...(groups.get(key) ?? []), t]);
    }
  }
  if (focused && !groups.has(focused)) groups.set(focused, []);

  const grantKeys = [...groups.keys()]
    .filter((k) => k)
    .sort((x, y) => byGrantStart(byId.get(x)!, byId.get(y)!));

  const rows: Row[] = [];
  for (const k of [...grantKeys, ...(groups.has('') ? [''] : [])]) {
    rows.push(k ? { kind: 'grant', key: k, node: byId.get(k), depth: 0, group: k } : { kind: 'nogrant', key: '(no grant)', depth: 0, group: '' });
    for (const { node: t, depth } of nested(groups.get(k)!)) {
      rows.push({ kind: 'task', key: `${k}/${t.id}`, node: t, depth, group: k });
    }
  }
  return rows;
}

/** A yyyy-mm-dd as local midnight, or null when there is none or the browser cannot read it ("TBD"). */
export function day(s: string): Date | null {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The day an action was added, in Vilnius; '' when unknown. */
export function addedDay(at: string): string {
  const d = at ? new Date(at) : null;
  return d && !Number.isNaN(d.getTime()) ? hubDate(d) : '';
}

/** An open action's bubble on the time axis. `nth`: bubbles on the same day before it (they sit side by side). */
export interface ActionMark {
  a: ActionItem;
  day: string;
  hollow: boolean;
  nth: number;
}

/**
 * Where each open action sits (old __GANTT_ACTROW, Actions.gs:594-604): on its deadline (filled), else on
 * the day it was added (hollow). `beside`: neither date; drawn after the bar end, before the "+".
 */
export function actionMarks(acts: readonly ActionItem[]): { marks: ActionMark[]; beside: ActionItem[] } {
  const marks: ActionMark[] = [];
  const beside: ActionItem[] = [];
  const taken = new Map<string, number>();
  for (const a of acts) {
    const hollow = !isCalendarDate(a.due);
    const on = hollow ? addedDay(a.at) : a.due;
    if (!on) {
      beside.push(a);
      continue;
    }
    const nth = taken.get(on) ?? 0;
    taken.set(on, nth + 1);
    marks.push({ a, day: on, hollow, nth });
  }
  return { marks, beside };
}

/**
 * [first day, last day], padded by a month. It covers every row's dates, today, and with `b` the rows'
 * output targets and open actions (where their bubbles sit: actionMarks), as the old draw() (DP:7249-7257).
 * Those from `b` count only from 2 years before to 5 years after the rows' dates and today: one far-off
 * date ("0202-05-15") would draw a grid line for each of thousands of months. Its marker sits on the edge.
 * A year around today if nothing is dated.
 */
export function timeDomain(rows: Row[], today: Date = new Date(), b?: Pick<GanttBundle, 'outputs' | 'openActions'>): [Date, Date] {
  const real = (ds: string[]) => ds.map(day).filter((d): d is Date => !!d);
  const ids = new Set(rows.flatMap((r) => (r.node ? [r.node.id] : [])));
  const days = real(rows.flatMap((r) => (r.node ? [r.node.start, r.node.end] : [])));
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (b) {
    const more: string[] = [];
    for (const o of b.outputs) if (ids.has(o.nodeId)) more.push(o.targetDate);
    for (const id of ids) more.push(...actionMarks(b.openActions[id] ?? []).marks.map((m) => m.day));
    const span = [...days, now].map(Number);
    const lo = new Date(Math.min(...span));
    const hi = new Date(Math.max(...span));
    lo.setFullYear(lo.getFullYear() - 2);
    hi.setFullYear(hi.getFullYear() + 5);
    days.push(...real(more).filter((d) => d >= lo && d <= hi));
  }
  if (days.length === 0) {
    return [new Date(today.getFullYear(), today.getMonth() - 6, 1), new Date(today.getFullYear(), today.getMonth() + 7, 1)];
  }
  days.push(now);
  const min = new Date(Math.min(...days.map(Number)));
  const max = new Date(Math.max(...days.map(Number)));
  return [new Date(min.getFullYear(), min.getMonth() - 1, 1), new Date(max.getFullYear(), max.getMonth() + 2, 1)];
}

/** Rows are this tall (.g-row in gantt.css): the dependency lines are drawn on this pitch. */
export const ROW_H = 30;

/** A dependency line: from the parent's row to the child's (indexes into the rows). */
export interface DepLink {
  from: number;
  to: number;
  /** The parent is in another grant group: dashed, darker (old `cross`). */
  cross: boolean;
}

/**
 * Dependency lines, old draw() (DP:7387-7392): each task with a bar whose parent also has a bar on the
 * chart. Only rows shown, so they follow the filters and Focus. A task listed under two grants gets a
 * line in each group: from its parent in the same group, else from the nearest row of its parent (dashed).
 * `hasBar(i)`: row i has a bar. The chart passes its own test, so a date it cannot read ("TBD") draws no line.
 */
export function depLinks(rows: Row[], hasBar: (i: number) => boolean = (i) => !!rows[i]!.node && isDated(rows[i]!.node!)): DepLink[] {
  const bars = new Map<string, number[]>();
  rows.forEach((r, i) => {
    if (r.kind === 'task' && r.node && hasBar(i)) bars.set(r.node.id, [...(bars.get(r.node.id) ?? []), i]);
  });
  const out: DepLink[] = [];
  rows.forEach((r, i) => {
    const n = r.node;
    if (r.kind !== 'task' || !n || !hasBar(i) || !n.parentId || n.parentId === n.id) return;
    const parents = bars.get(n.parentId);
    if (!parents) return;
    const same = parents.find((p) => rows[p]!.group === r.group);
    const from = same ?? parents.reduce((best, p) => (Math.abs(p - i) < Math.abs(best - i) ? p : best));
    out.push({ from, to: i, cross: same === undefined });
  });
  return out;
}

/** The elbow, in px: right from the parent's bar end, down, into the child's bar start (old path, DP:7392). */
export function depPath(endX: number, fromY: number, startX: number, toY: number): string {
  const r = (v: number) => Math.round(v * 10) / 10;
  return `M${r(endX)},${r(fromY)} H${r(Math.max(endX, startX - 8) + 4)} V${r(toY)} H${r(startX - 2)}`;
}

/** People on a task, grouped by role in the old display order. */
export function peopleByRole(b: GanttBundle, nodeId: string): { role: string; people: string[] }[] {
  return ROLE_ORDER
    .map((role) => ({
      role,
      people: b.assignments.filter((a) => a.nodeId === nodeId && a.role === role).map((a) => a.person).sort(),
    }))
    .filter((g) => g.people.length > 0);
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/** Type as shown on the chart; MS reads "MSc", as in the old Gantt. */
export function typeLabel(type: string): string {
  return type === 'MS' ? 'MSc' : type === GRANT_TYPE ? 'Grant' : type || 'Topic';
}

/** The bundle's people by name (lowerKey), as assignments name them. */
export const peopleByName = (b: Pick<GanttBundle, 'people'>): Map<string, GanttPerson> => new Map(b.people.map((p) => [lowerKey(p.name), p]));

/**
 * People drawn on a bar: the roles ticked, Focus at or above the minimum. A blank Focus, or no Persons
 * row, counts as 0 (old Gantt): Min focus 3 hides them, 0+ shows them. `byName`: the chart's, built once.
 */
export function barPeople(
  b: GanttBundle,
  nodeId: string,
  f: Filters,
  byName = peopleByName(b),
): { person: string; role: string; image: string; personId?: string }[] {
  return b.assignments
    .filter((a) => a.nodeId === nodeId && f.roles.includes(a.role))
    .filter((a) => (byName.get(lowerKey(a.person))?.focus ?? 0) >= f.minFocus)
    .sort((x, y) => byRole(x, y) || x.person.localeCompare(y.person))
    .map((a) => {
      const p = byName.get(lowerKey(a.person));
      return { person: a.person, role: a.role, image: p?.image ?? '', personId: p?.personId };
    });
}

/** Can `parentId` hold `childId`? Not itself, not one of its descendants, not a grant. */
export function canParent(b: GanttBundle, childId: string, parentId: string): boolean {
  const byId = new Map(b.nodes.map((n) => [n.id, n]));
  const p = byId.get(parentId);
  if (!p || isGrant(p) || parentId === childId) return false;
  const seen = new Set<string>();
  for (let cur: GanttNode | undefined = p; cur && !seen.has(cur.id); cur = cur.parentId ? byId.get(cur.parentId) : undefined) {
    if (cur.id === childId) return false;
    seen.add(cur.id);
  }
  return true;
}

/** Whole days from today (Vilnius, as the server counts) to `end` (negative = past), or null. */
export function daysLeft(end: string, today: string = hubDate()): number | null {
  return daysUntil(end, today);
}

export function fmtLeft(days: number): string {
  if (days === 0) return 'ends today';
  if (days > 0) return days > 60 ? `${Math.round(days / 30)} months left` : `${days} day${days === 1 ? '' : 's'} left`;
  const ago = -days;
  return `ended ${ago > 60 ? `${Math.round(ago / 30)} months` : `${ago} day${ago === 1 ? '' : 's'}`} ago`;
}
